/**
 * Parsing an uploaded students file (Add Student step) into rows for the wizard grid.
 *
 * Three layouts are read, told apart by the header row:
 *  1. the students template WITH a `Curriculum` column (requested with `curriculum_column=true`):
 *     Class holds the name without its curriculum prefix, resolved back to the stored class
 *     through the template's `Lists` sheet (see studentClassMatch.ts);
 *  2. the earlier template: no Curriculum column, qualified class labels ("Grade 1-A (FBISE)").
 *     Users keep uploading files they downloaded before the change, so this path is unchanged;
 *  3. a legacy file with one `Subjects` column (`;` / `,` / `|` separated).
 *
 * The same parser reads a workbook the backend filled in from a PDF, Word file or photo
 * (`POST data-upload/convert`): that workbook IS our template. Pure: no React, no I/O.
 */
import type {DataUploadConversion, DataUploadSkippedRow} from 'common/helpers/dataUpload'

import {nameRuleViolation, skippedRow} from './recordRules'
import {
  type CurriculumColumnClassMatch,
  NO_CURRICULUM_LABEL,
  parseClassLabel,
} from './studentClassMatch'
import {normalizeCsvHeader} from './teacherEntry'

/** Joins a subject and its class in a student's `subjects` entries: `${subject}|||${class}`. */
export const STUDENT_SUBJECT_DELIMITER = '|||'

/**
 * Ask the backend for the students template with a separate Curriculum column
 * (`curriculum_column=true` on the template download and on data-upload/convert). The layout is
 * opt-in because the portal parses this file itself; `parseStudentsRows` reads both layouts, so
 * a file downloaded before the switch still uploads.
 */
export const STUDENTS_CURRICULUM_COLUMN_ENABLED = true

/** First-sheet name of the students template; such a workbook is never sent for conversion. */
export const STUDENTS_TEMPLATE_SHEET = 'Students'

export type StudentCsvRow = {
  full_name: string
  /** The STORED class name once resolved; otherwise the class as written in the file. */
  class: string
  registrationNumber: string
  /** `${subject}|||${class}` entries, keyed by the same class name as `class`. */
  subjects: string[]
  /** Branch, level and curriculum unpacked from the class label or the `Lists` sheet. */
  branch?: string
  level?: string
  /**
   * As written in the file (a short code, "FBISE", or the Curriculum cell) until the row is
   * resolved to a wizard class; then the class's curriculum display name, which is what the
   * payload sends.
   */
  curriculum?: string
  /**
   * Curriculum-column layout only: what the user typed in the Class / Curriculum cells.
   * Validation messages show these, never the stored name. Not stored on the student.
   */
  typedClass?: string
  typedCurriculum?: string
  /** Curriculum-column layout only: why the Curriculum + Class pair could not be resolved. */
  unresolved?: 'none' | 'ambiguous'
  /**
   * Required values the file left blank. The row is kept (the grid highlights the blank cells
   * and Next waits until they are filled) instead of being dropped.
   */
  _missing?: StudentMissingField[]
  /** 1-based sheet row, used only in upload validation messages; never stored. */
  rowNumber: number
}

export type StudentMissingField = 'class' | 'registrationNumber'

/**
 * The server's class candidates per student (`report.needsReview[].choices.class`), keyed by
 * the lower-cased name: two curricula share the class name and the server does not guess.
 */
export const studentClassChoices = (
  conversion: Pick<DataUploadConversion, 'report'>
): Map<string, string[]> => {
  const out = new Map<string, string[]>()
  conversion.report.needsReview.forEach((row) => {
    const label = String(row.label ?? '')
      .trim()
      .toLowerCase()
    const choices = Array.isArray(row.choices?.class) ? row.choices.class.filter(Boolean) : []
    if (label && choices.length > 0) out.set(label, choices)
  })
  return out
}

/**
 * Identifies a student row across an import: its registration number, else its name (a row
 * the file left without one). Used to take back exactly the rows a prefill added.
 */
export const studentRowKey = (row: {registrationNumber?: unknown; full_name?: unknown}): string => {
  const reg = String(row?.registrationNumber ?? '')
    .trim()
    .toLowerCase()
  if (reg) return `reg:${reg}`
  return `name:${String(row?.full_name ?? '')
    .trim()
    .toLowerCase()}`
}

/** Fields of `StudentCsvRow` that only serve the upload and must not reach wizard state. */
export const STUDENT_ROW_TRANSIENT_FIELDS = [
  'rowNumber',
  'typedClass',
  'typedCurriculum',
  'unresolved',
] as const

/** "Subject 1".."Subject N" columns of the students template (headers already normalized). */
const SUBJECT_N_HEADER_RE = /^subject\s*\d+$/

export type StudentsFileError =
  | 'NO_DATA'
  | 'NO_NAME_COLUMN'
  | 'NO_CLASS_COLUMN'
  | 'NO_REG_COLUMN'
  | 'NO_SUBJECTS_COLUMN'

export interface ParseStudentsOptions {
  /**
   * Keep going when the Class, Registration number or Subjects column is missing: the rows are
   * kept with those cells blank. Only a missing name column still fails. Off by default, where a
   * missing column means "not our layout" and the file is better sent for conversion.
   */
  allowMissingColumns?: boolean
  /**
   * Lower-cased wizard class names; a class cell that is itself one of them is never unpacked
   * as a qualified label (label layout only).
   */
  wizardClassNames?: ReadonlySet<string>
  /**
   * Curriculum-column layout: resolves a Curriculum + Class cell pair to the stored class
   * (`resolveCurriculumColumnClass`). Without it such rows stay unresolved.
   */
  resolveCurriculumClass?: (cells: {
    curriculum: string
    classLabel: string
  }) => CurriculumColumnClassMatch
}

/** True when the header row has a `Curriculum` column: the curriculum-column layout. */
export const hasCurriculumColumn = (headerRow: readonly string[] | undefined): boolean =>
  (headerRow ?? []).some((header) => normalizeCsvHeader(header) === 'curriculum')

/**
 * Parses an uploaded students file. Rows without subjects are kept (subjects: []) so the
 * caller can report them, and so are rows with a blank class or registration number (marked
 * `_missing`: the school's own sheet often leaves them out, and the user fills them in the
 * grid). Only rows without a name are dropped, plus names the server would refuse, which are
 * listed in `skipped` (see recordRules.ts). Subject keys are built from the STORED class name,
 * never from the label: the grid, the subjects dropdown and the payload all match on it.
 */
export function parseStudentsRows(
  rows: string[][],
  options: ParseStudentsOptions = {}
): {rows: StudentCsvRow[]; skipped: DataUploadSkippedRow[]; error?: StudentsFileError} {
  const wizardClassNames = options.wizardClassNames ?? new Set<string>()
  const lenient = options.allowMissingColumns === true
  const fail = (error: StudentsFileError) => ({rows: [], skipped: [], error})
  if (rows.length < 2) {
    return fail('NO_DATA')
  }

  const headers = rows[0].map((h) => normalizeCsvHeader(h))
  const col = (candidates: string[]) => {
    for (const c of candidates) {
      const idx = headers.indexOf(c)
      if (idx >= 0) return idx
    }
    return -1
  }

  const idxFull = col(['full name', 'student name', 'name'])
  const idxFirst = col(['first name'])
  const idxLast = col(['last name'])
  const idxClass = col(['class', 'class name'])
  const idxCurriculum = col(['curriculum'])
  let idxReg = col(['registration number', 'registration no', 'reg no', 'reg', 'reg#', 'reg id'])
  const idxSubjects = col(['subjects', 'subject'])
  const subjectColumns = headers.flatMap((h, i) => (SUBJECT_N_HEADER_RE.test(h) ? [i] : []))

  if (idxFull < 0 && idxFirst < 0 && idxLast < 0) {
    return fail('NO_NAME_COLUMN')
  }
  if (idxClass < 0 && !lenient) {
    return fail('NO_CLASS_COLUMN')
  }
  if (idxReg < 0) {
    const idxAlt = col(['roll no', 'roll number', 'student id', 'student_id', 'id'])
    if (idxAlt >= 0) idxReg = idxAlt
  }
  if (idxReg < 0 && !lenient) {
    return fail('NO_REG_COLUMN')
  }
  if (idxSubjects < 0 && subjectColumns.length === 0 && !lenient) {
    return fail('NO_SUBJECTS_COLUMN')
  }

  const out: StudentCsvRow[] = []
  const skipped: DataUploadSkippedRow[] = []
  for (let r = 1; r < rows.length; r++) {
    const cells = rows[r].map((c) => c.trim())
    if (!cells.some((x) => x.length > 0)) continue

    let full = ''
    if (idxFull >= 0) {
      full = (cells[idxFull] || '').trim()
    } else {
      const first = idxFirst >= 0 ? (cells[idxFirst] || '').trim() : ''
      const last = idxLast >= 0 ? (cells[idxLast] || '').trim() : ''
      full = `${first} ${last}`.trim()
    }
    if (!full) continue
    // The name rules only. Not the "heading or note" rule: a student with nothing but a name
    // is exactly the row this parser keeps for the grid to complete.
    const violation = nameRuleViolation(full)
    if (violation) {
      skipped.push(skippedRow(full, violation))
      continue
    }

    const rawClass = idxClass >= 0 ? (cells[idxClass] || '').trim() : ''
    const registrationNumber = idxReg >= 0 ? (cells[idxReg] || '').trim() : ''
    const missing: StudentMissingField[] = []
    if (!rawClass) missing.push('class')
    if (!registrationNumber) missing.push('registrationNumber')

    let className = rawClass
    let branch: string | undefined
    let level: string | undefined
    let curriculum: string | undefined
    let typedClass: string | undefined
    let typedCurriculum: string | undefined
    let unresolved: StudentCsvRow['unresolved']

    // A blank class has nothing to resolve: the grid asks for it.
    if (rawClass && idxCurriculum >= 0) {
      // Curriculum-column layout: the pair names the class; the label alone does not.
      const curriculumCell = (cells[idxCurriculum] || '').trim()
      typedClass = rawClass
      typedCurriculum = curriculumCell || undefined
      const match: CurriculumColumnClassMatch = options.resolveCurriculumClass
        ? options.resolveCurriculumClass({curriculum: curriculumCell, classLabel: rawClass})
        : {kind: 'none'}
      if (match.kind === 'match') {
        className = match.className
        level = match.level || undefined
        branch = match.branch || undefined
        curriculum = match.curriculum || undefined
      } else {
        unresolved = match.kind
        const named = curriculumCell.toLowerCase() !== NO_CURRICULUM_LABEL.toLowerCase()
        curriculum = named && curriculumCell ? curriculumCell : undefined
      }
    } else if (rawClass) {
      const qualified = wizardClassNames.has(rawClass.toLowerCase())
        ? null
        : parseClassLabel(rawClass)
      if (qualified) {
        className = qualified.className
        level = qualified.level
        branch = qualified.branch
        curriculum = qualified.curriculum
      }
    }

    const subjectNames = [
      ...subjectColumns.map((i) => cells[i] || ''),
      ...(idxSubjects >= 0 ? (cells[idxSubjects] || '').split(/[;|,]+/g) : []),
    ]
      .map((s) => s.trim())
      .filter(Boolean)
    const seenSubjects = new Set<string>()
    const subjects = subjectNames
      .filter((name) => {
        const key = name.toLowerCase()
        if (seenSubjects.has(key)) return false
        seenSubjects.add(key)
        return true
      })
      .map((name) => `${name}${STUDENT_SUBJECT_DELIMITER}${className}`)
      // Subjects hang off a class: with none yet they are picked after the class.
      .filter(() => Boolean(className))

    out.push({
      full_name: full,
      class: className,
      registrationNumber,
      subjects,
      branch,
      level,
      curriculum,
      ...(typedClass ? {typedClass} : {}),
      ...(typedCurriculum ? {typedCurriculum} : {}),
      ...(unresolved ? {unresolved} : {}),
      ...(missing.length > 0 ? {_missing: missing} : {}),
      rowNumber: r + 1,
    })
  }

  return {rows: out, skipped}
}
